import { formatNumber } from '@awj/i18n';

import { useI18n } from '~/i18n/index.tsx';

/**
 * The list's footer: how many rows stand behind it, and the way through them.
 *
 * The reference panel's DataTable footer, element for element — the count at the reading edge,
 * numbered pages at the far end, the current one a soft accent chip rather than a solid fill.
 * A filled square in a footer pulls more weight than a page number deserves next to the list
 * above it.
 *
 * Numbers rather than the previous/next pair this used to be: on a list of forty pages, "next"
 * forty times is not navigation. The window is first, last, and the three around where you are,
 * with a gap standing in for what it skips.
 */
export function Pager({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t, locale } = useI18n();
  const count = t('common.totalRows', { count: formatNumber(locale, total) });

  if (totalPages <= 1) {
    return (
      <footer className="data-table__footer">
        <p className="data-table__info">{count}</p>
      </footer>
    );
  }

  return (
    <footer className="data-table__footer">
      <p className="data-table__info">{count}</p>

      <nav className="data-table__pagination" aria-label={t('common.pagination')}>
        <button
          type="button"
          className="data-table__page data-table__page--step"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t('common.previous')}
        </button>

        {pageWindow(page, totalPages).map((entry, index) =>
          entry === null ? (
            // eslint-disable-next-line react/no-array-index-key -- a gap has no identity of its own
            <span className="data-table__gap" key={`gap-${index}`} aria-hidden="true">
              …
            </span>
          ) : (
            <button
              type="button"
              key={entry}
              className={`data-table__page ${entry === page ? 'is-active' : ''}`}
              aria-current={entry === page ? 'page' : undefined}
              onClick={() => onPage(entry)}
            >
              {formatNumber(locale, entry)}
            </button>
          ),
        )}

        <button
          type="button"
          className="data-table__page data-table__page--step"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          {t('common.next')}
        </button>
      </nav>
    </footer>
  );
}

/**
 * First, last, and the three around where you are. `null` is a gap.
 *
 * Seven or fewer pages are all shown: a gap that hides one page is more furniture than the page
 * it replaced.
 */
function pageWindow(page: number, totalPages: number): (number | null)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const near = [page - 1, page, page + 1].filter((n) => n > 1 && n < totalPages);
  const shown = [1, ...near, totalPages];
  const out: (number | null)[] = [];

  for (const [index, n] of shown.entries()) {
    const previous = shown[index - 1];

    if (previous !== undefined && n - previous > 1) {
      out.push(null);
    }

    out.push(n);
  }

  return out;
}
